/**
 * Moteur de recommandation de formations, côté catalogue.
 *
 * Source de vérité unique : lib/courses-catalog.js (prérequis, catégories,
 * flags, durées marketing). Le backend Symfony ne fournit que l'état de
 * l'apprenant via dashboard-bootstrap (progressions, certifications, profil
 * d'onboarding). Utilisé par le dashboard ; getTargetSlugForProfile sert
 * aussi de fallback déterministe à l'API de recommandation d'onboarding.
 *
 * Principes (validés produit) :
 * - une seule « prochaine étape » actionnable, issue du parcours de l'objectif ;
 * - jamais une formation « coming soon », ni une formation dont les
 *   prérequis ne sont pas terminés, ni une formation déjà ouverte ;
 * - la recommandation persistée à l'onboarding prime tant que l'objectif
 *   n'a pas changé, sinon on retombe sur la cible déterministe.
 */

import {
    buildLearningPath,
    getAllCourses,
    getCourseBySlug,
} from "./courses-catalog";

// Mapping intérêt onboarding → catégories du catalogue.
// Partagé avec l'API de recommandation d'onboarding.
export const interestToCatalogCategories = {
    developers: ["developers"],
    nocode: ["nocode", "entrepreneurs"],
    ai: ["nocode", "developers"],
    entrepreneurs: ["entrepreneurs", "nocode"],
    explorer: ["developers", "entrepreneurs", "nocode"],
};

// Cible déterministe par objectif d'onboarding (fallback sans IA,
// et filet de sécurité si la reco persistée ne correspond plus à l'objectif).
const goalTargetSlugs = {
    "create-first-website": "formation-html",
    "develop-mobile-app": "formation-react-native",
    "launch-saas": "formation-nextjs",
    "get-tech-job": "formation-nextjs",
    "automate-tasks": "formation-n8n",
    "create-ai-agents": "formation-n8n",
    "connect-tools": "formation-n8n",
    "nocode-business": "formation-n8n",
    "daily-ai": "formation-claude-code",
    "code-with-ai": "formation-claude-code",
    "ai-automations": "formation-n8n",
    "ai-for-job": "formation-claude-code",
    "launch-business": "formation-n8n",
    "grow-business": "formation-n8n",
    "become-freelance": "formation-html",
    "put-site-online": "mettre-son-site-en-ligne-facilement",
    "discover-programming": "formation-python",
    "discover-nocode": "formation-n8n",
    "discover-ai": "formation-claude-code",
    "explore-all": "formation-python",
};

// Dernier filet par intérêt (aligné sur l'onboarding).
const fallbackByInterest = {
    developers: "formation-html",
    nocode: "formation-n8n",
    ai: "formation-claude-code",
    entrepreneurs: "formation-n8n",
    explorer: "formation-python",
};

/**
 * Cible déterministe pour un profil (objectif prioritaire, sinon intérêt).
 * Retourne toujours un slug valide et actionnable du catalogue : chaque
 * candidat est vérifié (existe et pas « coming soon ») avant d'être retenu.
 */
export function getTargetSlugForProfile(interest, goal) {
    const candidates = [
        goalTargetSlugs[goal],
        fallbackByInterest[interest],
        "formation-python",
    ];
    for (const slug of candidates) {
        if (!slug) continue;
        const course = getCourseBySlug(slug);
        if (course && !course.isSoon) return slug;
    }
    return "formation-python";
}

/**
 * Clé de traduction du libellé d'un objectif d'onboarding,
 * ou null si l'objectif est inconnu (valeur legacy).
 */
export function getGoalLabelKey(goal) {
    if (!goal || !goalTargetSlugs[goal]) return null;
    return `onboarding_goal_${goal.replace(/-/g, "_")}`;
}

/**
 * Extrait l'état d'apprentissage (slugs commencés / terminés, progression
 * par slug) du payload personal de dashboard-bootstrap.
 */
function buildLearnerState(personal) {
    const collections = [
        ...(personal.userCourses || []),
        ...(personal.userPrograms || []),
        ...(personal.subsPrograms || []),
        ...(personal.studentAccelerators || []),
    ];
    const progressions = personal.progressions || {};

    const startedSlugs = new Set();
    const completedSlugs = new Set();
    const progressionBySlug = {};

    collections.forEach((item) => {
        const product = item.product || item.program;
        if (!product || !product.slug) return;

        startedSlugs.add(product.slug);

        const progression = progressions[product.id];
        if (progression) {
            progressionBySlug[product.slug] = progression;
            if (progression.total >= 100) {
                completedSlugs.add(product.slug);
            }
        }
    });

    (personal.certifications || []).forEach((certification) => {
        const product = certification.course || certification.product;
        if (product && product.slug) {
            completedSlugs.add(product.slug);
        }
    });

    // Formations archivées : ouvertes un jour, donc jamais re-proposées
    // comme si elles étaient nouvelles (leur progression n'est plus exposée)
    (personal.archivedSlugs || []).forEach((slug) => {
        if (slug) startedSlugs.add(slug);
    });

    return { startedSlugs, completedSlugs, progressionBySlug };
}

/**
 * Construit les recommandations du dashboard.
 *
 * @param {object} params
 * @param {object} params.personal  payload personal de dashboard-bootstrap (avec .profile)
 * @param {string} params.locale    locale courante (fr, en...)
 * @returns {object|null} { needsGoal, pathCompleted, goal, interest, nextStep, complements }
 *          ou null si le payload ne contient pas le profil (backend pas à jour).
 */
export function buildDashboardRecommendations({ personal, locale }) {
    if (!personal || !personal.profile) return null;

    const profile = personal.profile;
    const goal = profile.goal || null;
    const interest = profile.category || null;

    // La reco persistée à l'onboarding prime, tant que l'objectif n'a
    // pas changé depuis et qu'elle reste actionnable.
    let persistedTargetSlug = null;
    if (
        profile.recommendedCourseSlug &&
        profile.recommendedCourseGoal === goal
    ) {
        const persisted = getCourseBySlug(profile.recommendedCourseSlug);
        if (persisted && !persisted.isSoon) {
            persistedTargetSlug = persisted.slug;
        }
    }

    // « dont-know » : cold start seulement si aucun parcours n'a été promis
    // à l'onboarding ; sinon on continue ce parcours (continuité de la
    // promesse BeBot, le sous-titre devient générique sans objectif).
    const needsGoal = !goal || (goal === "dont-know" && !persistedTargetSlug);

    const { startedSlugs, completedSlugs, progressionBySlug } =
        buildLearnerState(personal);

    // === Prochaine étape du parcours ===
    let nextStep = null;
    let pathCompleted = false;
    let pathSlugs = new Set();
    // Feuille de route complète (statut de chaque étape) — partagée par la
    // « prochaine étape » ET l'état « objectif atteint ».
    let journey = [];

    if (!needsGoal) {
        const targetSlug =
            persistedTargetSlug || getTargetSlugForProfile(interest, goal);

        const path = buildLearningPath(targetSlug);
        pathSlugs = new Set(path.map((course) => course.slug));

        const remaining = path.filter(
            (course) => !completedSlugs.has(course.slug),
        );

        // Étape courante = première non terminée (null si tout est terminé)
        const currentSlug = remaining.length > 0 ? remaining[0].slug : null;
        const inProgress = currentSlug
            ? startedSlugs.has(currentSlug)
            : false;
        const currentProgress =
            inProgress && progressionBySlug[currentSlug]
                ? Math.min(
                      99,
                      Math.round(progressionBySlug[currentSlug].total || 0),
                  )
                : 0;

        journey = path.map((item) => {
            const done = completedSlugs.has(item.slug);
            const current = item.slug === currentSlug;
            return {
                slug: item.slug,
                title: item.title,
                image: item.image,
                status: done ? "done" : current ? "current" : "upcoming",
                progress: done ? 100 : current ? currentProgress : 0,
            };
        });

        if (remaining.length === 0) {
            pathCompleted = true;
        } else {
            const stepIndex = path.findIndex(
                (item) => item.slug === currentSlug,
            );
            nextStep = {
                course: remaining[0],
                state: inProgress ? "resume" : "start",
                progress: currentProgress,
                stepNumber: stepIndex + 1,
                pathLength: path.length,
                previousTitle:
                    stepIndex > 0 ? path[stepIndex - 1].title : null,
                journey,
            };
        }
    }

    // === Compléments : actionnables immédiatement, dans l'univers de
    // l'apprenant, jamais redondants avec le parcours ===
    const interestCategories = interestToCatalogCategories[interest] || null;

    const pool = getAllCourses().filter(
        (course) =>
            !course.isSoon &&
            !startedSlugs.has(course.slug) &&
            !completedSlugs.has(course.slug) &&
            !pathSlugs.has(course.slug) &&
            (course.prerequisites || []).every((slug) =>
                completedSlugs.has(slug),
            ),
    );

    const complements = pool
        .map((course) => {
            let score = 0;
            if (
                interestCategories &&
                course.categories.some((category) =>
                    interestCategories.includes(category),
                )
            ) {
                score += 4;
            }
            if (course.lang === locale) score += 2;
            if (course.isPopular) score += 1;
            if (course.isNew) score += 0.5;
            // Cold start : privilégier les portes d'entrée
            if (needsGoal && course.level === "beginner") score += 1;
            return { course, score };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, 2)
        .map(({ course }) => {
            let reasonKey = "reco_reason_interest";
            if ((course.prerequisites || []).length > 0) {
                reasonKey = "reco_reason_ready";
            } else if (course.isPopular) {
                reasonKey = "reco_reason_popular";
            } else if (course.isNew) {
                reasonKey = "reco_reason_new";
            }
            return { course, reasonKey };
        });

    return {
        needsGoal,
        pathCompleted,
        goal,
        interest,
        nextStep,
        complements,
        journey,
    };
}

// === UN OUTIL QUAND AUCUNE FORMATION NE RÉPOND AU BESOIN ===
//
// Règle posée par Louis-Nicolas le 8 octobre 2026 : ce qu'on recommande en
// premier, c'est une formation ; quand le catalogue n'en a pas pour le besoin
// exprimé, on recommande un outil gratuit de /outils, parce que c'est ce qu'on
// a de mieux à offrir sur ce point. Le parcours de formations reste proposé
// derrière, comme la suite.
//
// Le catalogue ne couvre aujourd'hui ni l'idée, ni l'offre, ni la création de
// l'entreprise, ni les clients, ni les prix, ni l'administratif. Il couvre le
// manque de temps (automatiser) : là, la formation reste la recommandation.
// À REVOIR à chaque formation publiée sur la vente : retirer ici le besoin
// qu'elle couvre, et la formation reprendra la première place.
//
// Les clés sont celles du registre des outils (lib/tools/registry.js).
const TOOL_BY_STAGE = {
    // Pas d'outil pour trouver une idée : le parcours de démarrage donne au
    // moins la carte de ce qui l'attend.
    idea: "je-me-lance",
    offer: "combien-facturer",
    setup: "je-me-lance",
    clients: "devis",
};
const TOOL_BY_BLOCKER = {
    clients: "devis",
    pricing: "combien-facturer",
    // `time` est absent : la formation sur les automatisations y répond.
};
const COMPANY_STATUSES = ["eurl", "eurlIr", "sasu"];

// Ce que l'outil donne tout de suite, dit à la personne (tutoiement, comme le
// reste de l'onboarding). Le registre des outils vouvoie et écrit ses titres
// pour la recherche : on ne les reprend pas sur cet écran.
const TOOL_PITCH = {
    "je-me-lance": "Les étapes pour créer ta micro-entreprise, dans l'ordre.",
    "combien-facturer": "Le tarif qu'il te faut pour vivre de ton activité, calculé avec tes chiffres.",
    devis: "Un devis conforme, prêt à envoyer à ton prochain client.",
    "echeances-societe": "Les prochaines échéances de ta société, au jour près.",
    "my-situation": "Tes prochaines échéances, calculées d'après ton entreprise.",
};

// Le pictogramme pastel de chaque outil recommandable (public/images/tools).
// Dessinés pour cet écran : ceux des réponses (panneau, étiquette, crayon)
// viennent d'être vus deux écrans plus tôt, l'outil a le sien.
const TOOL_IMAGE = {
    "je-me-lance": "/images/tools/getting-started.webp",
    "combien-facturer": "/images/tools/day-rate.webp",
    devis: "/images/tools/quote.webp",
    "echeances-societe": "/images/tools/company-deadlines.webp",
    "my-situation": "/images/tools/deadlines.webp",
};

/** Le pictogramme d'un outil recommandé, ou null si la clé est inconnue. */
export function getToolImage(toolKey) {
    return TOOL_IMAGE[toolKey] || null;
}

/** La phrase qui présente un outil recommandé, ou null si la clé est inconnue. */
export function getToolPitch(toolKey) {
    return TOOL_PITCH[toolKey] || null;
}

/**
 * L'outil à recommander avant la formation, ou null quand une formation répond
 * au besoin (ou que la personne n'a exprimé aucun besoin de ce genre).
 *
 * Les outils calculent selon le droit français : l'appelant ne doit s'en
 * servir que pour quelqu'un qui travaille en France, en français.
 *
 * @param {{businessStage?: string, businessBlocker?: string, businessStatus?: string}} need
 * @returns {string|null} Une clé du registre des outils, ou "my-situation"
 *          pour la page « Votre situation » (ses échéances).
 */
export function getToolKeyForNeed({ businessStage, businessBlocker, businessStatus } = {}) {
    if (businessBlocker === "admin") {
        return COMPANY_STATUSES.includes(businessStatus) ? "echeances-societe" : "my-situation";
    }
    return TOOL_BY_BLOCKER[businessBlocker] || TOOL_BY_STAGE[businessStage] || null;
}
